import { useState } from 'react';
import { useCashu } from '@/contexts/CashuContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/useToast';
import { Wallet, Plus, Send, Download, Copy, Loader2 } from 'lucide-react';
import QRCode from 'qrcode';
import { Textarea } from '@/components/ui/textarea';

export function CashuWallet() {
  const { balance, isLoading, requestMint, checkMintQuote, mintTokens, sendTokens, receiveTokens, refreshBalance } = useCashu();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  
  // Deposit state
  const [depositAmount, setDepositAmount] = useState('');
  const [invoice, setInvoice] = useState('');
  const [mintHash, setMintHash] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  
  // Send state
  const [sendAmount, setSendAmount] = useState('');
  const [generatedToken, setGeneratedToken] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  // Receive state
  const [receiveToken, setReceiveToken] = useState('');
  const [isReceiving, setIsReceiving] = useState(false);

  const handleDeposit = async () => {
    const amount = parseInt(depositAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid amount',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { pr, hash } = await requestMint(amount);
      setInvoice(pr);
      setMintHash(hash);
      
      // Generate QR code
      const qr = await QRCode.toDataURL(pr.toUpperCase());
      setQrCode(qr);
      
      toast({
        title: 'Invoice Created',
        description: 'Pay the Lightning invoice to deposit sats',
      });
      
      // Start checking for payment
      setIsCheckingPayment(true);
      checkPayment(hash);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create deposit invoice',
        variant: 'destructive',
      });
    }
  };

  const checkPayment = async (hash: string) => {
    const maxAttempts = 60; // Check for 5 minutes (60 * 5 seconds)
    let attempts = 0;
    
    const interval = setInterval(async () => {
      attempts++;
      
      try {
        const isPaid = await checkMintQuote(hash);
        
        if (isPaid) {
          clearInterval(interval);
          await mintTokens(hash);
          await refreshBalance();
          
          setIsCheckingPayment(false);
          setInvoice('');
          setMintHash('');
          setQrCode('');
          setDepositAmount('');
          
          toast({
            title: 'Deposit Successful',
            description: 'Your wallet has been funded!',
          });
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setIsCheckingPayment(false);
          
          toast({
            title: 'Timeout',
            description: 'Payment check timed out. Please try again.',
            variant: 'destructive',
          });
        }
      } catch (error) {
        clearInterval(interval);
        setIsCheckingPayment(false);
        
        toast({
          title: 'Error',
          description: 'Failed to check payment status',
          variant: 'destructive',
        });
      }
    }, 5000); // Check every 5 seconds
  };

  const handleSend = async () => {
    const amount = parseInt(sendAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid amount',
        variant: 'destructive',
      });
      return;
    }

    if (amount > balance) {
      toast({
        title: 'Insufficient Balance',
        description: 'You do not have enough sats',
        variant: 'destructive',
      });
      return;
    }

    setIsSending(true);
    try {
      const token = await sendTokens(amount);
      setGeneratedToken(token);
      await refreshBalance();
      
      toast({
        title: 'Token Created',
        description: 'Share this token to send sats',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create token',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleReceive = async () => {
    if (!receiveToken.trim()) {
      toast({
        title: 'Invalid Token',
        description: 'Please paste a valid Cashu token',
        variant: 'destructive',
      });
      return;
    }

    setIsReceiving(true);
    try {
      const amount = await receiveTokens(receiveToken);
      await refreshBalance();
      
      setReceiveToken('');
      
      toast({
        title: 'Received!',
        description: `You received ${amount} sats`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to receive token. It may have already been claimed.',
        variant: 'destructive',
      });
    } finally {
      setIsReceiving(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Copied!',
        description: 'Copied to clipboard',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="gap-2 bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20 hover:text-white"
        >
          <Wallet className="h-4 w-4" />
          {isLoading ? '...' : `${balance} sats`}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Cashu Wallet
          </DialogTitle>
          <DialogDescription>
            Manage your ecash balance and send tips to strangers
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className="text-4xl font-bold">{balance}</span>
            <span className="text-xl text-muted-foreground">sats</span>
          </div>

          <Tabs defaultValue="deposit" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="deposit">
                <Plus className="h-4 w-4 mr-2" />
                Deposit
              </TabsTrigger>
              <TabsTrigger value="send">
                <Send className="h-4 w-4 mr-2" />
                Send
              </TabsTrigger>
              <TabsTrigger value="receive">
                <Download className="h-4 w-4 mr-2" />
                Receive
              </TabsTrigger>
            </TabsList>

            <TabsContent value="deposit" className="space-y-4 mt-4">
              {!invoice ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="deposit-amount">Amount (sats)</Label>
                    <Input
                      id="deposit-amount"
                      type="number"
                      placeholder="1000"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                    />
                  </div>
                  <Button onClick={handleDeposit} className="w-full">
                    Create Invoice
                  </Button>
                </>
              ) : (
                <div className="space-y-4">
                  {qrCode && (
                    <div className="flex justify-center">
                      <img src={qrCode} alt="Lightning Invoice QR" className="w-64 h-64" />
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Lightning Invoice</Label>
                    <div className="flex gap-2">
                      <Input value={invoice} readOnly className="font-mono text-xs" />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(invoice)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {isCheckingPayment && (
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Waiting for payment...
                    </div>
                  )}
                  
                  <Button
                    variant="outline"
                    onClick={() => {
                      setInvoice('');
                      setMintHash('');
                      setQrCode('');
                      setIsCheckingPayment(false);
                    }}
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="send" className="space-y-4 mt-4">
              {!generatedToken ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="send-amount">Amount (sats)</Label>
                    <Input
                      id="send-amount"
                      type="number"
                      placeholder="100"
                      value={sendAmount}
                      onChange={(e) => setSendAmount(e.target.value)}
                    />
                  </div>
                  <Button onClick={handleSend} disabled={isSending} className="w-full">
                    {isSending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Token'
                    )}
                  </Button>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Cashu Token</Label>
                    <Textarea
                      value={generatedToken}
                      readOnly
                      className="font-mono text-xs h-32"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button onClick={() => copyToClipboard(generatedToken)} className="flex-1">
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Token
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setGeneratedToken('');
                        setSendAmount('');
                      }}
                      className="flex-1"
                    >
                      Create New
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="receive" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="receive-token">Cashu Token</Label>
                <Textarea
                  id="receive-token"
                  placeholder="Paste Cashu token here..."
                  value={receiveToken}
                  onChange={(e) => setReceiveToken(e.target.value)}
                  className="font-mono text-xs h-32"
                />
              </div>
              <Button onClick={handleReceive} disabled={isReceiving} className="w-full">
                {isReceiving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Receiving...
                  </>
                ) : (
                  'Receive Token'
                )}
              </Button>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
